// Copyright (c) 2026. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { getLabelsetBelongingToGroup, getAllLabelsetsBelongingToGroup } from './FirebaseLabelsetsUtils';

/**
 * Get material mismatches by material type (only mismatched materials, excluding expected)
 * @param {Object} delivery - The delivery object
 * @param {Object} globalLabelsets - Global labelsets configuration
 * @returns {Object} Object with material label IDs as keys and counts as values (only mismatches)
 */
export function getMismatchesByMaterial(delivery, globalLabelsets) {
  if (!delivery || !globalLabelsets) {
    return {};
  }

  // Get the delivery's material labelset ID from delivery_labels
  const materialsLabelsetId = getLabelsetBelongingToGroup(
    Object.keys(delivery?.delivery_labels || {}),
    globalLabelsets,
    'materials',
  );

  if (!materialsLabelsetId) {
    return {};
  }

  // Get actual material counts from analytics
  const actualMaterial = delivery?.analytics?.data_per_labelset?.[materialsLabelsetId]?.label_counts || {};

  // Get expected material
  const expectedMaterialLabelId = delivery?.delivery_labels?.[materialsLabelsetId];

  if (!expectedMaterialLabelId) {
    return {};
  }

  // Return only mismatched materials (exclude the expected material)
  const mismatches = {};
  Object.entries(actualMaterial).forEach(([materialLabelId, count]) => {
    if (materialLabelId !== expectedMaterialLabelId) {
      mismatches[materialLabelId] = count;
    }
  });

  return mismatches;
}

/**
 * Calculate material mismatch statistics for a delivery
 * @param {Object} delivery - The delivery object
 * @param {Object} globalLabelsets - Global labelsets configuration
 * @returns {Object} Object containing mismatchCount, mismatchPercentage, and totalCount
 */
export function calculateMaterialMismatchCount(delivery, globalLabelsets) {
  if (!delivery || !globalLabelsets) {
    return { mismatchCount: 0, mismatchPercentage: 0, totalCount: 0 };
  }

  // Get the delivery's material labelset ID
  const materialsLabelsetId = getLabelsetBelongingToGroup(
    Object.keys(delivery?.delivery_labels || {}),
    globalLabelsets,
    'materials',
  );

  if (!materialsLabelsetId) {
    return { mismatchCount: 0, mismatchPercentage: 0, totalCount: 0 };
  }

  // Get all material counts (for total)
  const actualMaterial = delivery?.analytics?.data_per_labelset?.[materialsLabelsetId]?.label_counts || {};
  const totalCount = Object.values(actualMaterial).reduce((acc, count) => acc + count, 0);

  if (totalCount === 0) {
    return { mismatchCount: 0, mismatchPercentage: 0, totalCount: 0 };
  }

  // Get mismatches and calculate total mismatch count
  const mismatches = getMismatchesByMaterial(delivery, globalLabelsets);
  const mismatchCount = Object.values(mismatches).reduce((sum, count) => sum + count, 0);

  const mismatchPercentage = (100 / totalCount) * mismatchCount;

  return { mismatchCount, mismatchPercentage, totalCount };
}

/**
 * Get contaminants detection data by type
 * @param {Object} delivery - The delivery object
 * @param {Object} globalLabelsets - Global labelsets configuration
 * @returns {Object} Object with contaminant label IDs as keys and counts as values
 */
export function getContaminantsByType(delivery, globalLabelsets) {
  if (!delivery || !globalLabelsets) {
    return {};
  }

  const contaminantsLabelsetId = getLabelsetBelongingToGroup(
    Object.keys(delivery?.analytics?.data_per_labelset || {}),
    globalLabelsets,
    'contaminants',
  );

  if (!contaminantsLabelsetId) {
    return {};
  }

  return delivery?.analytics?.data_per_labelset?.[contaminantsLabelsetId]?.detection_counts ?? {};
}

/**
 * Calculate total contaminants detected in a delivery
 * @param {Object} delivery - The delivery object
 * @param {Object} globalLabelsets - Global labelsets configuration (optional, for future use)
 * @returns {number} Total count of contaminants detected
 */
export function calculateTotalContaminants(delivery, globalLabelsets = null) {
  if (!delivery) {
    return 0;
  }

  // Try to use the detection_counts format (newer format)
  const detectionCounts = getContaminantsByType(delivery, globalLabelsets);
  const detectionCountsValues = Object.values(detectionCounts);

  if (detectionCountsValues.length > 0) {
    return detectionCountsValues.reduce((sum, value) => (typeof value === 'number' ? sum + value : sum), 0);
  }
}

/**
 * Get bale IDs grouped by contaminant type from imported bales data
 * @param {Object} importedBalesData - The imported bales data dictionary
 * @param {Object} globalLabelsets - Global labelsets configuration
 * @param {number} detectionConfThreshold - Confidence threshold for detections (default 0.5)
 * @returns {Object} Object with contaminant type as key and array of bale IDs as value
 */
export function getBaleIdsByContaminant(importedBalesData, globalLabelsets, detectionConfThreshold = 0.5) {
  const result = {};

  if (!importedBalesData || typeof importedBalesData !== 'object' || !globalLabelsets) {
    return result;
  }

  const contaminantsLabelsetIds = getAllLabelsetsBelongingToGroup(globalLabelsets, 'contaminants');

  Object.entries(importedBalesData).forEach(([baleId, baleData]) => {
    const detectedObjects = baleData?.detections?.detected_objects || {};

    if (Object.keys(detectedObjects).length === 0) {
      return;
    }

    Object.values(detectedObjects).forEach((detection) => {
      if (contaminantsLabelsetIds.includes(detection?.labelset) && detection?.conf >= detectionConfThreshold) {
        const contaminantType = detection?.cls;
        if (!result[contaminantType]) {
          result[contaminantType] = [];
        }
        if (!result[contaminantType].includes(baleId)) {
          result[contaminantType].push(baleId);
        }
      }
    });
  });

  return result;
}

/**
 * Get bale IDs grouped by mismatch material type from imported bales data
 * Only returns materials that don't match the expected material
 * @param {Object} importedBalesData - The imported bales data dictionary
 * @param {Object} globalLabelsets - Global labelsets configuration
 * @param {string} expectedMaterialLabelId - The expected material label ID to compare against
 * @returns {Object} Object with material label ID as key and array of bale IDs as value (only mismatches)
 */
export function getBaleIdsByMismatchMaterial(importedBalesData, globalLabelsets, expectedMaterialLabelId) {
  const result = {};

  if (!importedBalesData || typeof importedBalesData !== 'object' || !globalLabelsets || !expectedMaterialLabelId) {
    return result;
  }

  Object.entries(importedBalesData).forEach(([baleId, baleData]) => {
    const materialLabelsetId = getLabelsetBelongingToGroup(
      Object.keys(baleData?.predicted_labels || {}),
      globalLabelsets,
      'materials',
    );
    const materialLabel = baleData?.predicted_labels?.[materialLabelsetId]?.label;

    // Only include if material exists and doesn't match expected
    if (materialLabel && materialLabel !== expectedMaterialLabelId) {
      if (!result[materialLabel]) {
        result[materialLabel] = [];
      }
      result[materialLabel].push(baleId);
    }
  });

  return result;
}
